import { useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { useUIStore } from '@/stores/ui.store';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ImpersonationBanner } from '@/components/shared/ImpersonationBanner';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function AppShell() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const fetchWorkspaces = useWorkspaceStore((s) => s.fetchWorkspaces);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      fetchWorkspaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
          <div className="space-y-3 w-64">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check approval status
  if (user?.approval_status === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }
  if (user?.approval_status === 'blocked') {
    return <Navigate to="/blocked" replace />;
  }

  return (
    <>
      <ImpersonationBanner />
      <div className="flex min-h-screen pt-[var(--impersonation-banner-height,0px)]">
      <Sidebar />
      <div
        className={cn(
          'flex flex-1 flex-col transition-all duration-200',
          // Desktop: offset by sidebar width
          'md:ml-[60px]',
          sidebarOpen && 'md:ml-60',
          // Mobile: no offset (sidebar is overlay)
          'ml-0'
        )}
      >
        <Header />
        <main className="flex-1 p-6 relative overflow-x-hidden overflow-y-auto min-w-0">
          {/* Background gradient mesh (matching old system) */}
          <div className="absolute inset-0 gradient-mesh pointer-events-none" />
          <div className="absolute top-0 right-1/4 w-96 h-96 blur-orb-purple opacity-50" />
          <div className="absolute bottom-1/4 left-0 w-64 h-64 blur-orb-blue opacity-50" />
          <div className="relative z-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
    </>
  );
}
